import React, { useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { differenceInMinutes, parse } from "date-fns";
import { omit } from "lodash";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import GreetingsMessage from "@/components/GreetingMessage";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import {
  CreateWorkScheduleInput,
  RuleKind,
  UpdateWorkScheduleInput,
} from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { format } from "@/utils/datetime";
import { displayError, isGraphqlError, isInvalidError } from "@/utils/errors";

import CalendarView from "./Tabs/CalendarView";
import ScheduleListView from "./Tabs/ScheduleListView";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const schedulerListViewQuery = graphql(`
  query SchedulerListView {
    coachViewer {
      id
      workSchedule {
        ...CoachWorkSchedule
      }
    }
  }

  fragment CoachWorkSchedule on WorkSchedule {
    id
    coachId
    rules {
      ...Rule
    }
  }

  fragment Rule on Rule {
    id
    date
    ruleType
    wday
    intervals {
      ...Interval
    }
  }

  fragment Interval on Interval {
    id
    ruleId
    startTime
    endTime
  }
`);

const createWorkScheduleMutation = graphql(`
  mutation CreateWorkSchedule($input: CreateWorkScheduleInput!) {
    createWorkSchedule(input: $input) {
      workSchedule {
        ...CoachWorkSchedule
      }
    }
  }
`);

const updateWorkScheduleMutation = graphql(`
  mutation UpdateWorkScheduleInput($input: UpdateWorkScheduleInput!) {
    updateWorkSchedule(input: $input) {
      coach {
        id
        workSchedule {
          ...CoachWorkSchedule
        }
      }
    }
  }
`);

export const endTimeValidation = yup
  .date()
  .presence()
  .nullable()
  .test(
    "is-greater",
    "End time must be greater than start time",
    function (endTime) {
      const { startTime } = this.parent;

      if (!startTime || !endTime) {
        return true;
      }

      return new Date(endTime) >= new Date(startTime);
    }
  );

export const validationSchema = yup
  .object()
  .shape({
    wdayRules: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            id: yup.string().notRequired(),
            date: yup.string().optional().nullable(),
            ruleType: yup.string().required().oneOf(Object.values(RuleKind)),
            wday: yup.string().optional().nullable(),
            intervals: yup
              .array()
              .of(
                yup
                  .object()
                  .shape({
                    id: yup.string().notRequired(),
                    ruleId: yup.string().notRequired(),
                    startTime: yup.date().presence().nullable(),
                    endTime: endTimeValidation,
                    _destroy: yup.boolean().notRequired(),
                  })
                  .defined()
              )
              .defined(),
            _destroy: yup.boolean().notRequired(),
          })
          .defined()
      )
      .defined(),
    dateRules: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            id: yup.string().notRequired(),
            key: yup.string().notRequired(),
            date: yup.string().optional().nullable(),
            ruleType: yup.string().required().oneOf(Object.values(RuleKind)),
            wday: yup.string().optional().nullable(),
            intervals: yup
              .array()
              .of(
                yup
                  .object()
                  .shape({
                    id: yup.string().notRequired(),
                    ruleId: yup.string().notRequired(),
                    startTime: yup.date().presence().nullable(),
                    endTime: endTimeValidation,
                    _destroy: yup.boolean().notRequired(),
                  })
                  .defined()
              )
              .defined(),
            _destroy: yup.boolean().notRequired(),
          })
          .defined()
      )
      .defined(),
  })
  .defined();

export type FormValues = yup.InferType<typeof validationSchema>;
export type WdayRule = FormValues["wdayRules"][0];
export type DateRule = FormValues["dateRules"][0];
const defaultFormValues: FormValues = {
  wdayRules: [
    {
      id: "",
      wday: "Sunday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Monday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Tuesday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Wednesday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Thursday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Friday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
    {
      id: "",
      wday: "Saturday",
      intervals: [],
      ruleType: RuleKind.Wday,
      date: null,
      _destroy: false,
    },
  ],
  dateRules: [],
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: "100vh" }}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const profilePanelQuery = graphql(`
  query MyProfile {
    coachViewer {
      id
      account {
        id
        email
      }
      profile {
        fullName
        avatarUrl
      }
    }
  }
`);

const SchedulerPage: React.FC = () => {
  const snackbar = useGlobalSnackbar();
  const [tabValue, setTabValue] = useState<number>(0);
  const { data } = useQuery(profilePanelQuery);
  const coachFullname = data?.coachViewer.profile?.fullName;
  const [createWorkSchedule] = useMutation(createWorkScheduleMutation);
  const [updateWorkSchedule] = useMutation(updateWorkScheduleMutation);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { data: workScheduleQuery } = useQuery(schedulerListViewQuery);
  const rules = workScheduleQuery?.coachViewer.workSchedule?.rules ?? [];

  const _getSchedule = (values = rules): FormValues => {
    return {
      wdayRules: values
        .filter((e) => e.ruleType === RuleKind.Wday)
        .map((e) => {
          return {
            ...omit(e, "__typename"),
            date: e.date ?? null,
            wday: e.wday ?? null,
            intervals: e.intervals.map((e) => {
              return {
                ...omit(e, "__typename"),
                startTime: parse(e.startTime ?? "", "HH:mm:ss", new Date()),
                endTime: parse(e.endTime ?? "", "HH:mm:ss", new Date()),
                _destroy: undefined,
              };
            }),
            _destroy: false,
          };
        }),
      dateRules: values
        .filter((e) => e.ruleType === RuleKind.Date)
        .map((e) => {
          return {
            ...omit(e, "__typename"),
            date: e.date ?? null,
            wday: e.wday ?? null,
            intervals: e.intervals.map((e) => {
              return {
                ...omit(e, "__typename"),
                startTime: parse(e.startTime ?? "", "HH:mm:ss", new Date()),
                endTime: parse(e.endTime ?? "", "HH:mm:ss", new Date()),
                _destroy: undefined,
              };
            }),
            _destroy: false,
          };
        }),
    };
  };
  const coachWorkSchedule: FormValues = _getSchedule();

  const workSchedule = coachWorkSchedule.wdayRules.length
    ? coachWorkSchedule
    : defaultFormValues;

  const submitMode = workScheduleQuery?.coachViewer.workSchedule
    ? "update"
    : "create";

  const formMethods = useForm<FormValues>({
    defaultValues: workSchedule,
    resolver: yupResolver(validationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  useEffect(() => {
    formMethods.reset(workSchedule);
  }, []);

  //when adding a new override rule using append,
  //it overrides the id field with it's own temp value.
  //So this function removes that temp id if the submit mode
  //is update, so that backend won't throw a 'Rule not found' exception.
  function transformId(id: string | undefined): string {
    if (id) {
      //the temp id from append exceeds the length of 10.
      return id.length > 10 ? "" : id;
    } else {
      return "";
    }
  }

  function displayError(error: any): string {
    if (error?.message == "Coach is invalid") {
      return "There is an error in your time schedule. Double check and fix it before submitting.";
    } else {
      return "There was an error in saving your work schedule. Please try again later.";
    }
  }

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    const combinedRules = [...formValues.wdayRules, ...formValues.dateRules];
    const createWorkScheduleInput: CreateWorkScheduleInput = {
      attributes: {
        coachId: data?.coachViewer.id,
        rulesAttributes: combinedRules?.map((e) => {
          return {
            ruleType: e.ruleType,
            date: e.date,
            wday: e.wday,
            intervalsAttributes: e.intervals?.map((e) => {
              return {
                startTime: format(
                  { timeofday: e?.startTime ?? new Date() },
                  "hh:mm aa"
                ),
                endTime: format(
                  { timeofday: e?.endTime ?? new Date() },
                  "hh:mm aa"
                ),
                _destroy: e?._destroy,
              };
            }),
          };
        }),
      },
    };
    const updateWorkScheduleInput: UpdateWorkScheduleInput = {
      id: workScheduleQuery?.coachViewer.workSchedule?.id ?? "",
      patch: {
        rulesAttributes: combinedRules?.map((e) => {
          const ruleObject = {
            id: transformId(e.id),
            ruleType: e.ruleType,
            date: e.date,
            wday: e.wday,
            intervalsAttributes: e.intervals?.map((e) => {
              return {
                id: e?.id,
                startTime: format(
                  { timeofday: e?.startTime ?? new Date() },
                  "hh:mm aa"
                ),
                endTime: format(
                  { timeofday: e?.endTime ?? new Date() },
                  "hh:mm aa"
                ),
                _destroy: e?._destroy,
              };
            }),
            _destroy: e._destroy,
          };
          return ruleObject;
        }),
      },
    };

    if (submitMode == "create") {
      try {
        const result = await createWorkSchedule({
          variables: { input: createWorkScheduleInput },
          refetchQueries: [
            {
              query: schedulerListViewQuery,
            },
          ],
        });
        const rules = result.data?.createWorkSchedule.workSchedule.rules ?? [];
        const schedule = _getSchedule(rules);
        formMethods.reset(schedule);
        snackbar.open("Successfully saved work schedule.");
      } catch (error: any) {
        snackbar.open(displayError(error));
      }
    } else {
      try {
        const result = await updateWorkSchedule({
          variables: { input: updateWorkScheduleInput },
          refetchQueries: [
            {
              query: schedulerListViewQuery,
            },
          ],
        });
        const rules =
          result.data?.updateWorkSchedule.coach.workSchedule?.rules ?? [];
        const schedule = _getSchedule(rules);
        formMethods.reset(schedule);
        snackbar.open("Successfully updated work schedule");
      } catch (error: any) {
        snackbar.open(displayError(error));
      }
    }
  };

  const onRemoveInterval = async (interval: WdayRule["intervals"][0]) => {
    const updateWorkScheduleInput: UpdateWorkScheduleInput = {
      id: workScheduleQuery?.coachViewer.workSchedule?.id ?? "",
      patch: {
        rulesAttributes: [
          {
            id: interval.ruleId,
            ruleType: RuleKind.Wday,
            intervalsAttributes: [
              {
                id: interval.id,
                _destroy: true,
              },
            ],
          },
        ],
      },
    };
    try {
      const result = await updateWorkSchedule({
        variables: { input: updateWorkScheduleInput },
      });
      const rules =
        result.data?.updateWorkSchedule.coach.workSchedule?.rules ?? [];
      const schedule = _getSchedule(rules);
      formMethods.setValue("wdayRules", schedule.wdayRules, {
        shouldDirty: true,
      });
    } catch (error: any) {
      snackbar.open(error.message);
    }
  };

  return (
    <>
      <GreetingsMessage fullName={coachFullname ?? "..."} />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            padding={4}
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            marginTop={2}
          >
            <Typography variant="h5">Manage Schedule</Typography>
            <Button
              type="submit"
              onClick={() => {
                if (!formMethods.formState.isValid) {
                  snackbar.open(
                    "There is an error in your time schedule. Double check and fix it before submitting."
                  );
                }
              }}
              disabled={formMethods.formState.isSubmitting}
            >
              {formMethods.formState.isSubmitting
                ? "Submitting.."
                : "Save schedule"}
            </Button>
          </Box>
          <Box sx={{ width: "100%", marginTop: 0 }} padding={4}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                sx={{ backgroundColor: "#fff" }}
                value={tabValue}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab
                  sx={{ textTransform: "none" }}
                  label="List View"
                  {...a11yProps(0)}
                />
                {Features.schedulerCalendarViewEnabled && (
                  <Tab
                    sx={{ textTransform: "none" }}
                    label="Calendar View"
                    {...a11yProps(1)}
                  />
                )}
              </Tabs>
            </Box>
            <CustomTabPanel value={tabValue} index={0}>
              <ScheduleListView onRemoveInterval={onRemoveInterval} />
            </CustomTabPanel>
            {Features.schedulerCalendarViewEnabled && (
              <CustomTabPanel value={tabValue} index={1}>
                <CalendarView />
              </CustomTabPanel>
            )}
          </Box>
        </form>
      </FormProvider>
    </>
  );
};

export default SchedulerPage;
